import { formatDistanceToNowStrict } from 'date-fns';

import { ConversationKind } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { HouseholdChat_ConversationListItemFragment } from '../graphql/HouseholdChat.generated';

const MAX_TITLE_LENGTH = 80;
const FALLBACK_TITLE = 'New conversation';
const VALIDATION_QUESTIONS_TITLE = 'Validation questions';

/**
 * Whether this row is one of the household's shared validation-questions
 * conversations rather than a chat the current user started. Those are listed
 * for everyone with access to the household, so they aren't the current user's
 * to delete.
 */
export function isValidationQuestionsConversation(
  conversation: HouseholdChat_ConversationListItemFragment
): boolean {
  return conversation.kind === ConversationKind.ValidationQuestions;
}

/**
 * Conversations have no title field; the first message (created_at ASC) is the
 * user's opening question, which we use as the row title. Falls back to a
 * generic label when a conversation somehow has no messages.
 *
 * Validation-questions conversations open with the agent's questions rather
 * than a user prompt, so their first message makes a poor title — they get a
 * fixed label instead.
 */
export function deriveConversationTitle(
  conversation: HouseholdChat_ConversationListItemFragment
): string {
  if (isValidationQuestionsConversation(conversation)) {
    return VALIDATION_QUESTIONS_TITLE;
  }

  const [firstMessage] = getNodes(conversation.messages);
  const content = firstMessage?.content?.trim();

  if (!content) {
    return FALLBACK_TITLE;
  }

  if (content.length <= MAX_TITLE_LENGTH) {
    return content;
  }

  return `${content.slice(0, MAX_TITLE_LENGTH).trimEnd()}…`;
}

/**
 * Relative "time since last activity" label for a conversation row, e.g.
 * "2 hours ago". Accepts the parsed Time scalar (Date) or a date-like value.
 */
export function formatConversationTimestamp(
  updatedAt: string | number | Date
): string {
  return formatDistanceToNowStrict(new Date(updatedAt), { addSuffix: true });
}
